import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
    Box,
    Flex,
    Icon,
    Text,
    IconButton,
    HStack,
    VStack,
    Button,
    Separator,
} from '@chakra-ui/react';
import {
    FiHome,
    FiUserPlus,
    FiSearch,
    FiSettings,
    FiUsers,
    FiFileText,
    FiMenu,
    FiLogOut,
    FiX,
    FiSlash,
    FiMapPin,
    FiUserCheck,
    FiDatabase,
    FiExternalLink,
} from 'react-icons/fi';
import { useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import NotificationBell from './NotificationBell';
import { LuPin, LuPinOff } from 'react-icons/lu';

// Simple Avatar component for initials display
const SimpleAvatar = ({ name, size = 'sm' }) => {
    const getInitials = (n) => {
        if (!n) return '?';
        const parts = n.split(' ');
        if (parts.length >= 2) {
            return (parts[0][0] + parts[1][0]).toUpperCase();
        }
        return n.substring(0, 2).toUpperCase();
    };

    const sizeMap = { sm: '32px', md: '40px', lg: '48px' };
    const fontSizeMap = { sm: '12px', md: '14px', lg: '16px' };

    return (
        <Box
            w={sizeMap[size]}
            h={sizeMap[size]}
            borderRadius="full"
            bg="purple.500"
            color="white"
            display="flex"
            alignItems="center"
            justifyContent="center"
            fontSize={fontSizeMap[size]}
            fontWeight="bold"
        >
            {getInitials(name)}
        </Box>
    );
};

const LinkItems = [
    { name: 'Dashboard', icon: FiHome, path: '/dashboard', roles: ['Admin', 'Editor', 'Editor Branch', 'User', 'User Branch'] },
    { name: 'Search', icon: FiSearch, path: '/search', roles: ['Admin', 'Editor', 'Editor Branch', 'User', 'User Branch'] },
    { name: 'Create Agent', icon: FiUserPlus, path: '/create-agent', roles: ['Admin', 'Editor', 'Editor Branch'] },
    { name: 'Agent Suggestions', icon: FiUserCheck, path: '/agent-suggestions', roles: ['Admin', 'Editor', 'Editor Branch'] },
    { name: 'Pending Uploads', icon: FiFileText, path: '/pending-uploads', roles: ['Admin', 'Editor', 'Editor Branch'] },
    { name: 'Pending Queue', icon: FiFileText, path: '/pending-queue', roles: ['Admin', 'Editor', 'Editor Branch'] },
    { name: 'Blacklist', icon: FiSlash, path: '/blacklist', roles: ['Admin', 'Editor', 'Editor Branch', 'User', 'User Branch'] },
    { name: 'City Ports', icon: FiMapPin, path: '/city-ports', roles: ['Admin', 'Editor', 'Editor Branch'] },
    { name: 'Master Data', icon: FiDatabase, path: '/master-data', roles: ['Admin', 'Editor', 'Editor Branch'] },
    { name: 'Users', icon: FiUsers, path: '/users', roles: ['Admin'] },
    { name: 'Settings', icon: FiSettings, path: '/settings', roles: ['Admin'] },
];

export default function Layout({ children }) {
    const [isOpen, setIsOpen] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [isPinned, setIsPinned] = useState(false);
    const [disableTransitions, setDisableTransitions] = useState(false);
    const hoverTimerRef = useRef(null);
    const justResumedRef = useRef(false);
    const resumeTimerRef = useRef(null);
    const onOpen = () => setIsOpen(true);
    const onClose = () => setIsOpen(false);

    // Track tab visibility to skip animation on tab return
    useEffect(() => {
        const handleVisibility = () => {
            if (document.visibilityState === 'visible') {
                justResumedRef.current = true;
                setDisableTransitions(true);

                clearTimeout(resumeTimerRef.current);
                resumeTimerRef.current = setTimeout(() => {
                    justResumedRef.current = false;
                    setDisableTransitions(false);
                }, 280);
            }
        };

        document.addEventListener('visibilitychange', handleVisibility);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibility);
            clearTimeout(hoverTimerRef.current);
            clearTimeout(resumeTimerRef.current);
        };
    }, []);

    const handleMouseEnter = useCallback(() => {
        clearTimeout(hoverTimerRef.current);
        // Longer delay immediately after tab resume to avoid jank
        const delay = justResumedRef.current ? 180 : 50;
        hoverTimerRef.current = setTimeout(() => {
            requestAnimationFrame(() => setIsHovered(true));
        }, delay);
    }, []);

    const handleMouseLeave = useCallback(() => {
        clearTimeout(hoverTimerRef.current);
        hoverTimerRef.current = setTimeout(() => {
            requestAnimationFrame(() => setIsHovered(false));
        }, 100);
    }, []);

    return (
        <Box minH="100vh" bg="gray.50">
            <SidebarContent
                onClose={onClose}
                display={{ base: 'none', md: 'flex' }}
                isHovered={isHovered}
                isPinned={isPinned}
                disableTransitions={disableTransitions}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                setIsPinned={setIsPinned}
                isMobile={false}
            />
            {/* Mobile Drawer */}
            {isOpen && (
                <Box
                    position="fixed"
                    top="0"
                    left="0"
                    right="0"
                    bottom="0"
                    zIndex="overlay"
                >
                    <Box
                        position="absolute"
                        top="0"
                        left="0"
                        right="0"
                        bottom="0"
                        bg="blackAlpha.600"
                        onClick={onClose}
                    />
                    <Box
                        position="absolute"
                        top="0"
                        left="0"
                        w="full"
                        maxW="xs"
                        h="full"
                        bg="white"
                        shadow="xl"
                    >
                        <SidebarContent onClose={onClose} isMobile={true} />
                    </Box>
                </Box>
            )}
            {/* Mobile Nav - Only hamburger menu now */}
            <MobileNav onOpen={onOpen} />
            <Box
                ml={{ base: 0, md: isPinned ? 60 : 16 }}
                p="4"
                transition="none"
            >
                {children}
            </Box>
        </Box>
    );
}

const SidebarContent = ({ onClose, isHovered, isPinned, disableTransitions, onMouseEnter, onMouseLeave, setIsPinned, isMobile, ...rest }) => {
    const { user, hasRole, logout } = useAuth();
    // For mobile, it's always expanded. For desktop, it depends on state.
    const isExpanded = isMobile || isPinned || isHovered;

    return (
        <Box
            transition={disableTransitions ? 'none' : 'width 0.2s cubic-bezier(0.4,0,0.2,1)'}
            bg="white"
            borderRight="1px"
            borderRightColor="gray.200"
            w={{ base: 'full', md: isExpanded ? 60 : 16 }}
            pos="fixed"
            h="full"
            display="flex"
            flexDirection="column"
            onMouseEnter={!isMobile ? onMouseEnter : undefined}
            onMouseLeave={!isMobile ? onMouseLeave : undefined}
            zIndex="sticky"
            overflowX="hidden"
            overflowY="hidden"
            contain="layout paint"
            {...rest}>
                {/* Logo Section */}
                <Flex h="32" alignItems="center" mx={isExpanded ? 8 : 2} justifyContent={{ base: 'space-between', md: isExpanded ? 'space-between' : 'center' }} mb={4} flexShrink={0}>
                    <img
                        src="https://i.ibb.co/DfXdPcVd/Quanterm-Official-Logo-High-Reso-01.png"
                        alt="Quanterm Logo"
                        loading="eager"
                        decoding="async"
                        style={{
                            height: '80px',
                            objectFit: 'contain',
                            opacity: isExpanded ? 1 : 0,
                            visibility: isExpanded ? 'visible' : 'hidden',
                            transition: disableTransitions ? 'none' : 'opacity 0.15s ease',
                            width: isExpanded ? 'auto' : 0,
                            position: isExpanded ? 'static' : 'absolute',
                        }}
                    />

                    {!isMobile && isExpanded && (
                        <IconButton
                            size="sm"
                            variant="ghost"
                            onClick={() => setIsPinned(!isPinned)}
                            aria-label={isPinned ? "Unpin Sidebar" : "Pin Sidebar"}
                            color={isPinned ? "purple.600" : "gray.400"}
                            _hover={{ color: "purple.500", bg: "purple.50" }}
                        >
                            <Icon as={isPinned ? LuPinOff : LuPin} boxSize="18px" />
                        </IconButton>
                    )}

                    <IconButton
                        display={{ base: 'flex', md: 'none' }}
                        onClick={onClose}
                        variant="outline"
                        aria-label="close menu"
                        size="sm"
                    >
                        <FiX />
                    </IconButton>
                </Flex>

                {/* Navigation Items */}
                <Box flex="1" overflowY="auto" overflowX="hidden">
                    {LinkItems.map((link) => {
                        if (!hasRole(link.roles)) return null;
                        return (
                            <NavItem
                                key={link.name}
                                icon={link.icon}
                                path={link.path}
                                isExpanded={isExpanded}
                                disableTransitions={disableTransitions}
                            >
                                {link.name}
                            </NavItem>
                        );
                    })}
                </Box>

                {/* Guideline Link */}
                <Box px={isExpanded ? 4 : 2} py={2}>
                    <Button
                        as="a"
                        href="https://quantermlogisitcsvietnam-my.sharepoint.com/:w:/g/personal/oversea12_hcm_quanterm_com_vn/IQC9u_UVpeQySb5i-W0JL8KUAcD4ByRsO8h-kRxyxHPYvlc?e=tCYw88"
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="ghost"
                        size={isExpanded ? 'sm' : 'xs'}
                        w="full"
                        justifyContent={isExpanded ? 'flex-start' : 'center'}
                        px={isExpanded ? 3 : 0}
                        color="gray.700"
                        _hover={{ bg: 'purple.50', color: 'purple.600' }}
                        leftIcon={<FiExternalLink />}
                        aria-label="Open Guideline"
                        title="Open Guideline"
                    >
                        {isExpanded && 'User Guide'}
                    </Button>
                </Box>

                {/* Bottom Section: Notification & User Profile */}
                <Box
                    borderTop="1px"
                    borderColor="gray.200"
                    p={isExpanded ? 4 : 2}
                    bg="white"
                    flexShrink={0}
                >
                    {/* Notification Bell */}
                    <Flex align="center" justify={isExpanded ? "space-between" : "center"} mb={3}>
                        {isExpanded && <Text fontSize="sm" fontWeight="medium" color="gray.600">Notifications</Text>}
                        <NotificationBell />
                    </Flex>

                    {isExpanded && <Separator mb={3} />}

                    {/* User Profile */}
                    <HStack spacing={3} mb={3} justifyContent={isExpanded ? "flex-start" : "center"}>
                        <SimpleAvatar
                            size={isExpanded ? "md" : "sm"}
                            name={user?.displayName || user?.email}
                        />
                        {isExpanded && (
                            <VStack align="start" spacing={0} flex={1} minW={0}>
                                <Text fontSize="sm" fontWeight="bold" color="gray.800" isTruncated maxW="full">
                                    {user?.displayName || user?.email}
                                </Text>
                                <Text fontSize="xs" color="gray.500">
                                    {user?.role}
                                </Text>
                            </VStack>
                        )}
                    </HStack>

                    {/* Sign Out Button */}
                    <Button
                        w="full"
                        size={isExpanded ? "sm" : "xs"}
                        variant="outline"
                        colorPalette="red"
                        onClick={logout}
                        justifyContent={isExpanded ? "flex-start" : "center"}
                        px={isExpanded ? 4 : 0}
                    >
                        <Icon as={FiLogOut} mr={isExpanded ? 2 : 0} />
                        {isExpanded && "Sign Out"}
                    </Button>
                </Box>
        </Box>
    );
};

const NavItem = ({ icon, children, path, isExpanded, disableTransitions, ...rest }) => {
    const location = useLocation();
    const isActive = location.pathname === path;

    return (
        <Link to={path} style={{ textDecoration: 'none' }}>
            <Flex
                align="center"
                p="4"
                mx={isExpanded ? 4 : 2}
                borderRadius="lg"
                role="group"
                cursor="pointer"
                bg={isActive ? 'purple.600' : 'transparent'}
                color={isActive ? 'white' : 'inherit'}
                _hover={{
                    bg: isActive ? 'purple.700' : 'purple.50',
                    color: isActive ? 'white' : 'purple.600',
                }}
                transition="background-color 0.15s ease, color 0.15s ease"
                justifyContent={isExpanded ? "flex-start" : "center"}
                {...rest}>
                {icon && (
                    <Icon
                        mr={isExpanded ? 4 : 0}
                        fontSize="16"
                        as={icon}
                    />
                )}
                <Text
                    opacity={isExpanded ? 1 : 0}
                    visibility={isExpanded ? "visible" : "hidden"}
                    transition={disableTransitions ? 'none' : 'opacity 0.15s ease'}
                    whiteSpace="nowrap"
                    w={isExpanded ? "auto" : 0}
                    overflow="hidden"
                >
                    {children}
                </Text>
            </Flex>
        </Link>
    );
};

const MobileNav = ({ onOpen, ...rest }) => {
    return (
        <Flex
            ml={{ base: 0, md: 60 }}
            px={{ base: 4, md: 4 }}
            height="16"
            alignItems="center"
            bg="white"
            borderBottomWidth="1px"
            borderBottomColor="gray.200"
            display={{ base: 'flex', md: 'none' }}
            {...rest}>
            <IconButton
                onClick={onOpen}
                variant="outline"
                aria-label="open menu"
                size="sm"
            >
                <FiMenu />
            </IconButton>
            <Text ml={4} fontSize="lg" fontWeight="bold" color="purple.600">
                Agent Directory
            </Text>
        </Flex>
    );
};
